import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Upload, Paperclip, Loader2, ArrowRight } from "lucide-react";
import { HOME } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const fieldCls =
  "w-full bg-white border border-brand-border rounded-lg px-4 py-3 text-brand-ink placeholder:text-brand-muted focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 transition-colors";
const labelCls = "block text-xs font-display font-semibold uppercase tracking-wider text-brand-slate mb-2";

const DualPortal = () => {
  const [clientLoading, setClientLoading] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const submitClient = async (e) => {
    e.preventDefault();
    setClientLoading(true);
    const data = new FormData(e.currentTarget);
    const payload = Object.fromEntries(data.entries());
    try {
      await axios.post(`${API}/job-postings`, payload);
      toast.success("Job posted — we'll be in touch within one business day.");
      e.target.reset();
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setClientLoading(false);
    }
  };

  const submitCandidate = async (e) => {
    e.preventDefault();
    setCandidateLoading(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      await axios.post(`${API}/resume-submissions`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Resume received. A recruiter will review within 48 hours.");
      form.reset();
      setFileName("");
    } catch (err) {
      toast.error("Upload failed. Please retry with a PDF or DOC under 10MB.");
    } finally {
      setCandidateLoading(false);
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-white">
      <div className="px-5 md:px-10 lg:px-16">
        <div className="flex flex-col items-center text-center mb-14 md:mb-20">
          <div className="text-xs md:text-sm font-display font-semibold tracking-[0.2em] uppercase text-brand-red mb-3">
            Get Started
          </div>
          <h2 className="section-heading section-heading-center font-display font-bold text-4xl md:text-5xl text-brand-ink">
            Hire Top Talent. Find Your Next Opportunity.
          </h2>
          <p className="mt-6 max-w-3xl text-brand-slate text-base md:text-lg leading-relaxed">
            Whether you&apos;re looking to build a high-performing team or
            searching for your next career move, SNV Elite Solutions connects
            employers with exceptional professionals through fast, reliable,
            and AI-powered recruitment solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* CLIENT FORM */}
          <motion.form
            id="post-job"
            data-testid={HOME.clientForm}
            onSubmit={submitClient}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="bg-white rounded-2xl border border-brand-border p-6 md:p-10 card-soft"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-lg bg-brand-blue-light flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-brand-blue" strokeWidth={2} />
              </div>
              <div>
                <div className="text-xs font-display font-semibold uppercase tracking-wider text-brand-blue">For Companies</div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-brand-ink leading-tight">
                  Post a Job
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div>
                <label className={labelCls}>Company</label>
                <input required name="company" placeholder="Your company" className={fieldCls} data-testid={HOME.clientFormCompany} />
              </div>
              <div>
                <label className={labelCls}>Contact Name</label>
                <input required name="contactName" placeholder="Your name" className={fieldCls} data-testid={HOME.clientFormContactName} />
              </div>
              <div>
                <label className={labelCls}>Work Email</label>
                <input required type="email" name="email" placeholder="you@company.com" className={fieldCls} data-testid={HOME.clientFormEmail} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input required name="phone" placeholder="e.g. +1 555 123 4567" className={fieldCls} data-testid={HOME.clientFormPhone} />
              </div>
              <div>
                <label className={labelCls}>Job Title</label>
                <input required name="jobTitle" placeholder="e.g. Senior QA Engineer" className={fieldCls} data-testid={HOME.clientFormJobTitle} />
              </div>
              <div>
                <label className={labelCls}>Location</label>
                <input required name="location" placeholder="City / Remote" className={fieldCls} data-testid={HOME.clientFormLocation} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Role Description</label>
                <textarea
                  required
                  name="description"
                  rows={4}
                  placeholder="Role summary, must-have skills, seniority…"
                  className={`${fieldCls} resize-none`}
                  data-testid={HOME.clientFormDescription}
                />
              </div>

              <label className="md:col-span-2 flex items-start gap-3 bg-brand-blue-light/40 border border-brand-blue/20 rounded-lg px-4 py-4 cursor-pointer hover:border-brand-blue/60 transition-colors">
                <input
                  required
                  type="checkbox"
                  name="consent"
                  data-testid="client-form-consent"
                  className="mt-1 w-5 h-5 flex-shrink-0 accent-brand-blue cursor-pointer"
                />
                <span className="text-sm text-brand-slate leading-relaxed">
                  <strong className="text-brand-ink">I consent to be contacted.</strong>{" "}
                  I agree that SNV Elite Solutions may use the contact details
                  above to reach out to me via <strong>Email</strong>,{" "}
                  <strong>SMS/Text</strong>, and phone regarding this hiring
                  brief and related recruiting communications. Message
                  frequency may vary and message &amp; data rates may apply.
                  Reply <strong>STOP</strong> to opt out at any time. Consent
                  is not a condition of any purchase. See our{" "}
                  <Link to="/privacy" target="_blank" className="text-brand-red font-semibold link-underline">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" target="_blank" className="text-brand-red font-semibold link-underline">
                    Terms &amp; Conditions
                  </Link>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={clientLoading}
              data-testid={HOME.clientFormSubmit}
              className="mt-6 md:mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-md transition-colors disabled:opacity-60"
            >
              {clientLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {clientLoading ? "Sending…" : "Submit Brief"}
              {!clientLoading && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
          </motion.form>

          {/* CANDIDATE FORM */}
          <motion.form
            id="submit-resume"
            data-testid={HOME.candidateForm}
            onSubmit={submitCandidate}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="bg-brand-ink rounded-2xl p-6 md:p-10 text-white"
            encType="multipart/form-data"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-lg bg-brand-red flex items-center justify-center">
                <Upload className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div>
                <div className="text-xs font-display font-semibold uppercase tracking-wider text-brand-red">For Candidates</div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-white leading-tight">
                  Submit Resume
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div>
                <label className={`${labelCls} !text-white/60`}>Full Name</label>
                <input required name="name" placeholder="Your name" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/25 transition-colors" data-testid={HOME.candidateFormName} />
              </div>
              <div>
                <label className={`${labelCls} !text-white/60`}>Email</label>
                <input required type="email" name="email" placeholder="you@email.com" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/25 transition-colors" data-testid={HOME.candidateFormEmail} />
              </div>
              <div>
                <label className={`${labelCls} !text-white/60`}>Phone</label>
                <input required name="phone" placeholder="e.g. +1 555 123 4567" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/25 transition-colors" data-testid={HOME.candidateFormPhone} />
              </div>
              <div>
                <label className={`${labelCls} !text-white/60`}>Target Role</label>
                <input required name="role" placeholder="e.g. Software Engineer" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/25 transition-colors" data-testid={HOME.candidateFormRole} />
              </div>
              <div className="md:col-span-2">
                <label className={`${labelCls} !text-white/60`}>Short Message</label>
                <textarea
                  required
                  name="message"
                  rows={3}
                  placeholder="A brief note about your background & availability."
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/25 transition-colors resize-none"
                  data-testid={HOME.candidateFormMessage}
                />
              </div>

              <label className="md:col-span-2 flex items-center justify-between gap-4 border border-dashed border-white/25 rounded-lg px-4 py-4 cursor-pointer hover:border-brand-red transition-colors">
                <span className="flex items-center gap-3 min-w-0">
                  <Paperclip className="w-4 h-4 text-brand-red flex-shrink-0" />
                  <span className="text-white/85 truncate">
                    {fileName || "Attach resume (PDF, DOC, DOCX · max 10MB)"}
                  </span>
                </span>
                <span className="font-display font-semibold text-brand-red text-sm flex-shrink-0">Browse</span>
                <input
                  ref={fileRef}
                  required
                  type="file"
                  name="resume"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid={HOME.candidateFormResume}
                  onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
                />
              </label>

              <label className="md:col-span-2 flex items-start gap-3 bg-white/5 border border-white/15 rounded-lg px-4 py-4 cursor-pointer hover:border-brand-red/60 transition-colors">
                <input
                  required
                  type="checkbox"
                  name="consent"
                  data-testid="candidate-form-consent"
                  className="mt-1 w-5 h-5 flex-shrink-0 accent-brand-red cursor-pointer"
                />
                <span className="text-sm text-white/85 leading-relaxed">
                  <strong className="text-white">I consent to be contacted.</strong>{" "}
                  I agree that SNV Elite Solutions may use the contact details
                  above to reach out to me via <strong>Email</strong>,{" "}
                  <strong>SMS/Text</strong>, and phone regarding this
                  application and related recruiting communications. Message
                  frequency may vary and message &amp; data rates may apply.
                  Reply <strong>STOP</strong> to opt out at any time. Consent
                  is not a condition of employment. See our{" "}
                  <Link to="/privacy" target="_blank" className="text-brand-red font-semibold link-underline">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" target="_blank" className="text-brand-red font-semibold link-underline">
                    Terms &amp; Conditions
                  </Link>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={candidateLoading}
              data-testid={HOME.candidateFormSubmit}
              className="mt-6 md:mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-red hover:bg-brand-red-dark text-white font-display font-semibold rounded-md transition-colors disabled:opacity-60"
            >
              {candidateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {candidateLoading ? "Uploading…" : "Send Resume"}
              {!candidateLoading && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default DualPortal;
